// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { JSX, lazy, Suspense, useEffect, useState } from "react"
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom"
import { Construction } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Card, CardContent } from "@/components/ui/card"
import { Item, ItemContent, ItemTitle } from "@/components/ui/item"
import { getDiagram, type DiagramKind } from "@/services/diagramsService"
import { isUserInMapAppsAdmins, useAuth } from "@/hooks/useAuth"
import { AppLayout } from "@/layout/AppLayout"
import LoginPage from "@/pages/Login"
import ChatPage from "@/pages/Chat"
import PositWorkbenchPage from "@/pages/PositWorkbench"

import SurveyInterface from "@/components/survey-catalogue/SurveyInterface"
import NewSurveyForm from "@/components/survey-catalogue/NewSurveyForm"
import SurveyDetailPage from "@/components/survey-catalogue/SurveyDetailPage"
import AllJournalArticlesPage from "@/components/journal-articles/pages/AllJournalArticlesPage"
import RecentlyAddedPage from "@/components/journal-articles/pages/RecentlyAddedPage"
import OpenAuthorContactsPage from "@/components/journal-articles/pages/OpenAuthorContactsPage"
import PrBulkReviewPage from "@/components/journal-articles/pages/PrBulkReviewPage"
import PrLitReviewOverviewPage from "@/components/journal-articles/pages/PrLitReviewOverviewPage"
import TopicPage from "@/components/journal-articles/pages/TopicPage"
import PrLitReviewCollectionPage from "@/components/journal-articles/pages/PrLitReviewCollectionPage"
import RsExtractionCollectionPage from "@/components/journal-articles/pages/RsExtractionCollectionPage"
import UserCollectionPage from "@/components/journal-articles/pages/UserCollectionPage"
import BiblioSourcesDetailPage from "@/components/shared/BiblioSourcesDetailPage"
import { JournalArticlesProvider } from "@/components/journal-articles/JournalArticlesContext"
import AllSourcesInterface from "@/components/all-sources/AllSourcesInterface"
import { AllSourcesProvider } from "@/components/all-sources/AllSourcesContext"
import AllReportsPage from "@/components/reports/pages/AllReportsPage"
import RecentlyAddedReportsPage from "@/components/reports/pages/RecentlyAddedReportsPage"
import ReportTypePage from "@/components/reports/pages/ReportTypePage"
import NeedsReviewReportsPage from "@/components/reports/pages/NeedsReviewReportsPage"
import { NEEDS_REVIEW_PATH, RETIRED_NEEDS_REVIEW_PATHS } from "@/components/reports/reportsRoutes"
import { ReportsProvider } from "@/components/reports/ReportsContext"
import DatasetsInterface from "@/components/datasets/DatasetsInterface"
import EditDatasetPage from "@/components/datasets/EditDatasetPage"
import NewDatasetForm from "@/components/datasets/NewDatasetForm"
import NewVersionPage from "@/components/datasets/NewVersionPage"
import PfInterface from "@/components/pf-modelling/PfInterface"
import HpcJobsInterface from "@/components/hpc-jobs/HpcJobsInterface"
import HpcJobLogsView from "@/components/hpc-jobs/HpcJobLogsView"
import HpcWorkflowDetailPage from "@/components/hpc-jobs/HpcWorkflowDetailPage"
import HpcArrayJobDetailPage from "@/components/hpc-jobs/HpcArrayJobDetailPage"
import HpcJobsDebugInterface from "@/components/hpc-jobs/HpcJobsDebugInterface"
import DiskUsageTable from "@/components/resource-usage/DiskUsageTable"
import TokenUsagePage from "@/components/resource-usage/TokenUsagePage"
import AdminDiskUsageTable from "@/components/resource-usage/AdminDiskUsageTable"
import AdminTokenUsagePage from "@/components/resource-usage/AdminTokenUsagePage"
import ResourceUsageDebugInterface from "@/components/resource-usage/ResourceUsageDebugInterface"
import PrClustersInterface from "@/components/parasite-rate/PrClustersInterface"
import PrPolygonsInterface from "@/components/parasite-rate/PrPolygonsInterface"
import RsDataInterface from "@/components/rs-data/RsDataInterface"
import RsDataPlotPage from "@/components/rs-data/RsDataPlotPage"
import DiagramsInterface from "@/components/diagrams/DiagramsInterface"

// The two Diagram_Editor pages are lazy-loaded so each kind's heavy dependency
// (`@excalidraw/excalidraw` for the Excalidraw page, `mermaid` for the Mermaid
// page) is only downloaded when a diagram of that kind is opened, and neither
// bundle enters the other's chunk or the main app bundle (Requirements 1.4,
// 5.1, 5.6). All other diagram routes stay eager, like their siblings.
const DiagramEditorPage = lazy(() => import("@/components/diagrams/DiagramEditorPage"))
const MermaidEditorPage = lazy(() => import("@/components/diagrams/MermaidEditorPage"))

/**
 * The shared loading/error shell for the editor dispatcher, matching the shell
 * the editor pages themselves use so the transition from "resolving kind" to
 * "editor open" is seamless (Requirement 5.1).
 *
 * @param props - The shell inputs.
 * @param props.error - An error message to show, or null to show the spinner.
 * @returns The Card-based loading or error shell.
 */
function DiagramEditorShell({ error }: { error: string | null }): JSX.Element {
  return (
    <>
      <title>Diagram - MAP Apps Platform</title>
      <Card className="flex min-h-0 flex-1 flex-col shadow-none">
        <CardContent className="flex min-h-0 flex-1 flex-col items-center justify-center">
          {error !== null ? (
            <Item variant="outline" className="w-auto">
              <ItemContent>
                <ItemTitle className="text-destructive">Error: {error}</ItemTitle>
              </ItemContent>
            </Item>
          ) : (
            <LoadingSpinner loadingContent="Loading diagram..." />
          )}
        </CardContent>
      </Card>
    </>
  )
}

/**
 * Dispatch the Diagram_Editor by Diagram_Kind, keyed on the diagram id so
 * moving to another diagram (for example after "Save as a copy") mounts a fresh
 * editor with its own lock, draft, and content (Requirement 5.1).
 *
 * The URL `/diagrams/:diagramId` is kind-agnostic (embeds and shared links
 * carry no kind), so this dispatcher fetches the diagram to learn its kind on a
 * cold direct navigation, then renders the lazy page for that kind. The chosen
 * page fetches the diagram again on mount to load its content; that small extra
 * GET is accepted for now rather than threading the already-fetched diagram
 * into both pages' load effects.
 *
 * @returns The shared shell while the kind resolves, an error shell on failure,
 *   or the lazily-loaded editor page for the resolved kind.
 */
function DiagramEditorRoute(): JSX.Element {
  const { diagramId } = useParams<{ diagramId: string }>()
  const { getAccessToken } = useAuth()
  const [kind, setKind] = useState<DiagramKind | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (diagramId === undefined) {
      setError("No diagram id in the URL")
      return
    }
    let cancelled: boolean = false
    // Reset when the id changes so a new diagram re-resolves its kind rather
    // than briefly rendering the previous diagram's editor.
    setKind(null)
    setError(null)
    void (async (): Promise<void> => {
      try {
        const token: string = await getAccessToken()
        const response = await getDiagram(token, diagramId)
        if (!cancelled) {
          setKind(response.diagram.kind)
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "Failed to load the diagram")
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [diagramId, getAccessToken])

  if (error !== null) {
    return <DiagramEditorShell error={error} />
  }
  if (kind === null) {
    return <DiagramEditorShell error={null} />
  }

  return (
    <Suspense fallback={<DiagramEditorShell error={null} />}>
      {kind === "mermaid" ? (
        <MermaidEditorPage key={diagramId} />
      ) : (
        <DiagramEditorPage key={diagramId} />
      )}
    </Suspense>
  )
}

function UnderDevelopment(): JSX.Element {
  return (
    <>
      <title>MAP Apps Platform</title>
      <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center px-4">
        <Construction className="size-12 text-primary" />
        <h1>Tool Under Development</h1>
        <p className="text-muted-foreground max-w-sm">
          This section is currently being built. Check back soon.
        </p>
      </div>
    </>
  )
}

function NoAccess(): JSX.Element {
  return (
    <>
      <title>MAP Apps Platform</title>
      <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center px-4">
        <Construction className="size-12 text-primary" />
        <h1>No Access</h1>
        <p className="text-muted-foreground max-w-sm">
          You do not have access to this tool currently. Please contact the Data Engineering Team if you feel you should have access.
        </p>
      </div>
    </>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }): JSX.Element | null {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppLayoutRoute(): JSX.Element {
  return (
    <ProtectedRoute>
      <AppLayout>
        <JournalArticlesProvider>
          <AllSourcesProvider>
            <ReportsProvider>
              <Outlet />
            </ReportsProvider>
          </AllSourcesProvider>
        </JournalArticlesProvider>
      </AppLayout>
    </ProtectedRoute>
  )
}

export default function AppRoutes(): JSX.Element {

  const { user } = useAuth()
  const userIsAdmin = isUserInMapAppsAdmins(user)

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AppLayoutRoute />}>
        <Route path="/" element={<Navigate to="/ai/exploratory-literature" replace />} />

        {/* AI Agents */}
        <Route
          path="/ai/exploratory-literature"
          element={
            <ChatPage key="exploratory-literature-review" agent="exploratory-literature-review" />
          }
        />
        <Route
          path="/ai/survey-variable"
          element={<ChatPage key="survey-variable-discovery" agent="survey-variable-discovery" />}
        />
        <Route
          path="/ai/deep-literature"
          element={<ChatPage key="deep-literature-review" agent="deep-literature-review" />}
        />

        {/* Survey Catalogue */}
        <Route path="/survey-catalogue" element={<SurveyInterface />} />
        <Route path="/survey-catalogue/new" element={<NewSurveyForm />} />
        <Route path="/survey-catalogue/:surveyId" element={<SurveyDetailPage />} />

        <Route path="/articles" element={<AllJournalArticlesPage />} />
        <Route path="/articles/recently-added" element={<RecentlyAddedPage />} />
        <Route path="/articles/open-author-contacts" element={<OpenAuthorContactsPage />} />
        {userIsAdmin ? (
          <Route path="/articles/pr-bulk-review" element={<PrBulkReviewPage />} />
        ) : (
          <Route path="/articles/pr-bulk-review" element={<NoAccess />} />
        )}
        <Route path="/articles/pr-lit-review-overview" element={<PrLitReviewOverviewPage />} />
        {/* Collection routes are type-specific so a dedicated page mounts per
            type. The PR/RS routes are declared before the generic one; the bare
            /articles/collection/:collectionId serves user collections (and is
            the fallback for any old link — a PR/RS id landing there resolves as
            not-found and redirects to the all-articles view). */}
        <Route path="/articles/collection/pr/:collectionId" element={<PrLitReviewCollectionPage />} />
        <Route path="/articles/collection/rs/:collectionId" element={<RsExtractionCollectionPage />} />
        <Route path="/articles/collection/:collectionId" element={<UserCollectionPage />} />
        <Route path="/articles/topic/:topicSlug" element={<TopicPage />} />
        <Route path="/articles/:biblioId" element={<BiblioSourcesDetailPage />} />

        {/* All Sources — provider is mounted in AppLayoutRoute above, so these
            routes share a single cached state instance. Visible in all
            environments, matching the sidebar nav. */}
        <Route path="/all-sources" element={<AllSourcesInterface />} />
        <Route path="/all-sources/:biblioId" element={<BiblioSourcesDetailPage />} />

        {/* Reports — provider is mounted in AppLayoutRoute above, so the list
            pages and the detail page share one cached state instance. The
            static and type routes are declared before /reports/:biblioId so
            they are not captured as ids. */}
        <Route path="/reports" element={<AllReportsPage />} />
        <Route path="/reports/recently-added" element={<RecentlyAddedReportsPage />} />
        <Route path={NEEDS_REVIEW_PATH} element={<NeedsReviewReportsPage />} />
        {/* The retired Unclassified and Non-Report Records type URLs redirect to
            Needs Review. Declared before the :reportTypeSlug route, which
            would otherwise fall back to All Reports for them. */}
        {RETIRED_NEEDS_REVIEW_PATHS.map((path: string) => (
          <Route key={path} path={path} element={<Navigate to={NEEDS_REVIEW_PATH} replace />} />
        ))}
        <Route path="/reports/type/:reportTypeSlug" element={<ReportTypePage />} />
        <Route path="/reports/:biblioId" element={<BiblioSourcesDetailPage section="reports" />} />

        {/* Datasets */}
        <Route path="/datasets" element={<DatasetsInterface />} />
        <Route path="/datasets/new" element={<NewDatasetForm />} />
        <Route path="/datasets/edit/:datasetId" element={<EditDatasetPage />} />
        <Route path="/datasets/:datasetId/new-version" element={<NewVersionPage />} />
        <Route path="/datasets/:datasetId" element={<DatasetsInterface />} />

        {/* Diagrams — admin-only, gated exactly like /articles/pr-bulk-review.
            The editor is lazy-loaded (Requirement 1.4), so it is wrapped in
            Suspense; the browser at /diagrams stays eager like its siblings. */}
        {userIsAdmin ? (
          <>
            <Route path="/diagrams" element={<DiagramsInterface />} />
            <Route path="/diagrams/:diagramId" element={<DiagramEditorRoute />} />
          </>
        ) : (
          <>
            <Route path="/diagrams" element={<NoAccess />} />
            <Route path="/diagrams/:diagramId" element={<NoAccess />} />
          </>
        )}

        {/* Modelling */}
        <Route path="/modelling/pf" element={<PfInterface />} />

        {/* Computing */}
        <Route path="/computing/posit" element={<PositWorkbenchPage />} />
        <Route path="/computing/hpc" element={<HpcJobsInterface />} />
        <Route path="/computing/hpc/admin" element={<HpcJobsInterface isAdmin />} />
        <Route path="/computing/hpc/logs/:jobId" element={<HpcJobLogsView />} />
        <Route path="/computing/hpc/workflow/:workflowId" element={<HpcWorkflowDetailPage />} />
        <Route path="/computing/hpc/array-job/:jobId" element={<HpcArrayJobDetailPage />} />
        <Route
          path="/computing/resource-usage"
          element={<Navigate to="/computing/resource-usage/disk" replace />}
        />
        <Route path="/computing/resource-usage/disk" element={<DiskUsageTable />} />
        <Route path="/computing/resource-usage/disk/admin" element={<AdminDiskUsageTable />} />
        <Route path="/computing/resource-usage/token" element={<TokenUsagePage />} />
        <Route
          path="/computing/resource-usage/token/admin"
          element={<AdminTokenUsagePage />}
        />

        {/* Parasite Rate */}
        <Route
          path="/data/parasite-rate"
          element={<Navigate to="/data/parasite-rate/clusters" replace />}
        />
        <Route path="/data/parasite-rate/clusters" element={<PrClustersInterface />} />
        <Route path="/data/parasite-rate/polygons" element={<PrPolygonsInterface />} />

        {/* Dev-only routes */}
        {import.meta.env.VITE_ENVIRONMENT === "dev" && (
          <>
            {/* National surveillance. The path carries the view (plots|table)
                and optional country (ISO3 or "all") and metric segments:
                /data/rs/national/:view/:country?/:metric?. The bare level path
                redirects to the plots default. */}
            <Route
              path="/data/rs/national"
              element={<Navigate to="/data/rs/national/plots" replace />}
            />
            <Route
              path="/data/rs/national/:view/:country?/:metric?"
              element={<RsDataInterface level="national" />}
            />
            {/* Subnational surveillance. Subnational carries two extra
                dimensions the national path does not — admin level and admin
                unit — so its path is deeper:
                /data/rs/subnational/:view/:country?/:adminLevel?/:adminUnit?/:metric?
                where :adminUnit is a map_admin_id or the "all" token (All Admin
                Units, the default) and :metric only applies in All-Admin-Units
                mode (mirroring how national's metric only applies in
                All-Countries mode). The bare level path redirects to plots. */}
            <Route
              path="/data/rs/subnational"
              element={<Navigate to="/data/rs/subnational/plots" replace />}
            />
            <Route
              path="/data/rs/subnational/:view/:country?/:adminLevel?/:adminUnit?/:metric?"
              element={<RsDataInterface level="subnational" />}
            />
            {/* Full-page single plot (separate from the level views above).
                National identifies a plot by country + metric; subnational
                identifies it by country + admin level + admin unit + metric, so
                the two have different path shapes. */}
            <Route
              path="/data/rs/national/full-page-plot/:country/:metric"
              element={<RsDataPlotPage level="national" />}
            />
            <Route
              path="/data/rs/subnational/full-page-plot/:country/:adminLevel/:adminUnit/:metric"
              element={<RsDataPlotPage level="subnational" />}
            />
            <Route
              path="/computing/resource-usage/debug"
              element={<ResourceUsageDebugInterface />}
            />
            <Route path="/computing/hpc/debug" element={<HpcJobsDebugInterface />} />
          </>
        )}

        <Route path="*" element={<UnderDevelopment />} />
      </Route>
    </Routes>
  )
}
